import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response.data,
  (error) => {
    const { response, config } = error;
    if (response) {
      const { status, data } = response;
      console.error(
        `[API] ${status} ${config?.method?.toUpperCase()} ${config?.url}`,
        data?.error || data?.message || data
      );
      if (status === 401) {
        const path = window.location.pathname;
        const isAuthPage = path.startsWith('/login') || path.startsWith('/verify-email') || path.startsWith('/reset-password') || path.startsWith('/mfa-verify');
        localStorage.removeItem('token');
        if (!isAuthPage) {
          window.location.href = '/login';
        }
      }
    } else {
      // Network error or request never sent
      console.error(`[API] Network error — ${config?.method?.toUpperCase()} ${config?.url}`, error.message);
    }
    return Promise.reject(error);
  }
);

// Health Check
export const fetchHealthCheck = async () => {
  return await api.get('/api/health');
};

// Patient APIs
// ⟳ R30-5 — a patient SEARCH TERM never goes in a URL (Cloud Run's request log
// records query strings). A params object carrying `q` is sent as
// POST /api/patients/search; the backend refuses `GET /api/patients?q=`.
export const fetchPatients = async (facilityIdOrParams) => {
  let query = '';
  if (facilityIdOrParams && typeof facilityIdOrParams === 'object' && facilityIdOrParams.q !== undefined) {
    const { q, ...rest } = facilityIdOrParams;
    return await api.post('/api/patients/search', { ...rest, q });
  }
  if (facilityIdOrParams && typeof facilityIdOrParams === 'object') {
    const qs = new URLSearchParams(facilityIdOrParams).toString();
    if (qs) query = `?${qs}`;
  } else if (facilityIdOrParams) {
    query = `?facility_id=${facilityIdOrParams}`;
  }
  return await api.get(`/api/patients${query}`);
};

export const fetchPatientById = async (id) => {
  return await api.get(`/api/patients/${id}`);
};

export const createPatient = async (patientData) => {
  return await api.post('/api/patients', patientData);
};

export const updatePatient = async (id, patientData) => {
  return await api.put(`/api/patients/${id}`, patientData);
};

// Soft-delete (archive) a patient. A free-text reason is required by the server.
export const deletePatient = async (id, reason) => {
  return await api.delete(`/api/patients/${id}`, { data: { reason } });
};

// Restore a previously archived patient.
export const restorePatient = async (id) => {
  return await api.post(`/api/patients/${id}/restore`);
};

// Revert one demographic field to its source (sync plan Step 5): drops the human-edit
// lock so the next source update applies. `field` is a WIRE name (e.g. postal_code).
export const revertPatientFieldToSource = async (id, field) => {
  return await api.post(`/api/patients/${id}/fields/${encodeURIComponent(field)}/revert-to-source`);
};

// ── Patient account merge (Admin only) ─────────────────────────────────────────
// Preview describes what would move and which collisions get resolved before
// anything is written; merge runs the whole thing atomically server-side.
export const previewPatientMerge = async (survivorId, loserId) => {
  const params = new URLSearchParams({ survivor: survivorId, loser: loserId });
  return await api.get(`/api/admin/patients/merge-preview?${params.toString()}`);
};

export const mergePatients = async (survivorId, loserId, reason) => {
  return await api.post('/api/admin/patients/merge', { survivorId, loserId, reason });
};

// ── Duplicate-review queue (Admin only) ────────────────────────────────────────
// Items come from sync patient matching. Resolving as "link" runs the merge
// server-side and closes the item in the same transaction.
export const fetchPatientReviews = async (status = 'open') => {
  const params = new URLSearchParams({ status });
  return await api.get(`/api/patient-reviews?${params.toString()}`);
};

// payload: { action: 'link'|'keep'|'restore'|'confirm'|'split'|'dismiss', candidatePatientId?, reason?, note? }
export const resolvePatientReview = async (id, payload) => {
  return await api.post(`/api/patient-reviews/${id}/resolve`, payload);
};

// ── Hold-review queue (plan Step 6) ────────────────────────────────────────────
// Encounters accepted from the source but not yet billable. Facility-FILTERED, not
// facility-scoped: the id comes from the app picker and any staff user may work
// holds, exactly as on the worklist.
export const fetchHolds = async (facilityId) => {
  const params = new URLSearchParams({ facility_id: facilityId });
  return await api.get(`/api/holds?${params.toString()}`);
};

// ── Sync Health (plan Step 15) ─────────────────────────────────────────────────
// Lag, volume vs. the weekday baseline, the absence timer, the four consistency
// jobs and holds by reason. Counts and ids only; facility-FILTERED like holds.
export const fetchSyncHealth = async (facilityId) => {
  const params = new URLSearchParams({ facility_id: facilityId });
  return await api.get(`/api/sync-health?${params.toString()}`);
};

// ── Unapplied patient cash (plan Step 12, S12-d) ───────────────────────────────
// Sync payments not yet applied to a claim, ONE ROW PER ALLOCATION. Facility-FILTERED,
// like the hold queue. Response: { data: [...], count, stale_after_days }.
export const fetchUnappliedCash = async (facilityId) => {
  const params = new URLSearchParams({ facility_id: facilityId });
  return await api.get(`/api/unapplied-cash?${params.toString()}`);
};

// ⟳ Step 14 (S14-g) — the three resolution actions, one allocation each. Nothing is deleted.
export const applyUnappliedCash = async (allocationId, claimId) => {
  return await api.post(`/api/unapplied-cash/${allocationId}/apply`, { claim_id: claimId });
};
export const refundUnappliedCash = async (allocationId, { reference, reason }) => {
  return await api.post(`/api/unapplied-cash/${allocationId}/refund`, { reference, reason });
};
export const voidUnappliedCash = async (allocationId, reason) => {
  return await api.post(`/api/unapplied-cash/${allocationId}/void`, { reason });
};

// payload: { resolution: 'applied'|'rejected'|'dismissed'|'link_to_existing', note?, link_claim_id? }
//
// 🔴 `link_to_existing` (D27, plan Step 9) REQUIRES `link_claim_id`, and it must
// be one of the candidates the hold itself offered. The server runs R9's ordered
// transaction on it — clear the placeholder's source identity, void the
// placeholder, stamp the surviving claim — so there is no version of this call
// that links to "whichever claim looked right"; the target is explicit.
export const resolveHold = async (id, payload) => {
  return await api.post(`/api/holds/${id}/resolve`, payload);
};

// Claim APIs
export const fetchClaims = async (facilityId, facilityIds) => {
  const params = new URLSearchParams();
  if (facilityId != null) {
    params.set('facility_id', facilityId);
  } else if (facilityIds && facilityIds.length > 0) {
    params.set('facility_ids', facilityIds.join(','));
  }
  const qs = params.toString();
  return await api.get(`/api/claims${qs ? `?${qs}` : ''}`);
};

// Revenue stats — aggregated on the server via SQL aggregate functions.
// Pass facilityId for a single facility, facilityIds[] for a subset,
// or neither to get stats across all facilities.
export const fetchRevenueStats = async (facilityId, facilityIds) => {
  const params = new URLSearchParams();
  if (facilityId != null) {
    params.set('facility_id', facilityId);
  } else if (facilityIds && facilityIds.length > 0) {
    params.set('facility_ids', facilityIds.join(','));
  }
  const qs = params.toString();
  return await api.get(`/api/claims/revenue${qs ? `?${qs}` : ''}`);
};

export const fetchARStats = async (facilityId, facilityIds) => {
  const params = new URLSearchParams();
  if (facilityId != null) {
    params.set('facility_id', facilityId);
  } else if (facilityIds && facilityIds.length > 0) {
    params.set('facility_ids', facilityIds.join(','));
  }
  const qs = params.toString();
  return await api.get(`/api/claims/ar${qs ? `?${qs}` : ''}`);
};

export const fetchClaimById = async (id) => {
  return await api.get(`/api/claims/${id}`);
};

export const createClaim = async (claimData) => {
  return await api.post('/api/claims', claimData);
};

export const updateClaim = async (id, claimData) => {
  return await api.put(`/api/claims/${id}`, claimData);
};

export const updateClaimNotes = async (id, notes) => {
  return await api.put(`/api/claims/${id}`, { notes });
};

export const deleteClaim = async (id) => {
  return await api.delete(`/api/claims/${id}`);
};

export const voidClaim = async (id, voidReason) => {
  return await api.patch(`/api/claims/${id}/void`, { void_reason: voidReason });
};

export const submitClaim = async (id) => {
  return await api.patch(`/api/claims/${id}/submit`);
};

// Reassign the payer at a given COB level. The server decides in-place vs rebill
// from claim state; see PATCH /api/claims/:id/change-payer.
export const changeClaimPayer = async (id, payload) => {
  return await api.patch(`/api/claims/${id}/change-payer`, payload);
};

// File a corrected/replacement claim (837 frequency 7) to the same payer.
export const correctClaim = async (id, payload) => {
  return await api.patch(`/api/claims/${id}/correct`, payload);
};

// Transmit an 837 frequency-8 void to withdraw a claim already accepted at the payer.
export const voidClaimAtPayer = async (payload) => {
  return await api.post('/api/claimmd/void', payload);
};

// Facility APIs
export const fetchFacilities = async () => {
  return await api.get('/api/facilities');
};

export const createFacility = async (facilityData) => {
  return await api.post('/api/facilities', facilityData);
};

export const updateFacilityProfile = async (id, profileData) => {
  return await api.patch(`/api/facilities/${id}`, profileData);
};

export const updateFacilityStatus = async (id, status, suspendReason) => {
  return await api.patch(`/api/facilities/${id}/status`, { status, suspendReason });
};

// Claim.MD clearinghouse APIs (full-API integration; replaces the Trizetto SFTP flow)
// Transmits the generated claim(s) as 837P/837I to Claim.MD and returns the
// per-claim acknowledgements (accepted/rejected + reason).
export const submitClaimsToClaimMd = async (claimIds) => {
  return await api.post('/api/claimmd/submit', { claimIds });
};

// Builds the 837 file(s) for preview/download without transmitting.
export const generateClaimMdEdi = async (claimIds) => {
  return await api.post('/api/claimmd/generate-edi', { claimIds });
};

// Pulls delayed claim-status updates from Claim.MD and applies them to claims.
export const syncClaimMdStatus = async () => {
  return await api.post('/api/claimmd/responses/sync', {});
};

// On-demand pull of newly received ERAs from Claim.MD into remit_batches.
export const syncClaimMdEras = async () => {
  return await api.post('/api/claimmd/era/sync', {});
};

// Realtime eligibility (270/271) via Claim.MD. `params` mirrors the documented
// eligdata fields (payerid, ins_name_l/f, ins_number, ins_dob, pat_rel, fdos,
// prov_npi, prov_taxid, …). Returns the parsed 271 benefit data.
export const checkEligibility = async (params) => {
  return await api.post('/api/claimmd/eligibility', params);
};

// Initiate a payer appeal for a claim; returns a Claim.MD-hosted appeal form link.
// Body: { claimId } (resolved to claimmd_id server-side) plus optional contact* fields.
export const createClaimMdAppeal = async (payload) => {
  return await api.post('/api/claimmd/appeal', payload);
};

// Remove claim(s) from Claim.MD's Manage Claims menu. Body: { claimIds: [...] }
// and/or { claimmdIds: [...] } (internal claimIds are resolved to claimmd_id).
export const archiveClaimMdClaims = async (payload) => {
  return await api.post('/api/claimmd/archive', payload);
};

// Initiate provider enrollment with a payer; returns a Claim.MD-hosted link.
// Body: { payerid, enrollType, provTaxid, provNpi?, provNameL?, contact?, ... }.
export const enrollClaimMdProvider = async (payload) => {
  return await api.post('/api/claimmd/enroll', payload);
};

// ── Clearinghouse read-only views (transmission log, ERA inbox, claim activity) ──

// Our local record of 837 batches uploaded to Claim.MD.
export const fetchClaimMdFiles = async () => {
  return await api.get('/api/claimmd/files');
};

// List ERAs as Claim.MD sees them (passthrough). Optional filters: NewOnly, PayerID, CheckDate…
export const fetchClaimMdEras = async (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return await api.get(`/api/claimmd/era${qs ? `?${qs}` : ''}`);
};

// Open a single ERA's remittance PDF (by Claim.MD eraid) in a new tab.
export const openClaimMdEraPdf = async (eraid) => {
  const blob = await api.get(`/api/claimmd/era/${eraid}/pdf?download=1`, { responseType: 'blob' });
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Raw 835 X12 document for an ERA (as text).
export const fetchClaimMdEra835 = async (eraid) => {
  return await api.get(`/api/claimmd/era/${eraid}/835`);
};

// One-off status lookup for a single Claim.MD claim id (does not advance the poll cursor).
export const fetchClaimMdClaimResponses = async (claimmdId) => {
  return await api.get(`/api/claimmd/claims/${claimmdId}/responses`);
};

// Clearinghouse notes stored for one of our claims (by internal claim id).
export const fetchClaimMdClaimNotes = async (claimId) => {
  return await api.get(`/api/claimmd/claims/${claimId}/notes`);
};

// Field modifications Claim.MD recorded for one of our claims (by internal claim id).
export const fetchClaimMdClaimModifications = async (claimId) => {
  return await api.get(`/api/claimmd/claims/${claimId}/modifications`);
};

// Trizetto APIs (legacy — retained for reference; no longer used by the Worklist)
export const submitToTrizetto = async (claimIds) => {
  return await api.post('/api/trizetto/submit', { claimIds });
};

export const fetchGeneratedClaimsForEdi = async (facilityId) => {
  return await api.get(`/api/trizetto/generated-claims?facility_id=${facilityId}`);
};

export const fetchTrizettoBatches = async () => {
  return await api.get('/api/trizetto/batches');
};

export const checkTrizettoStatus = async (facilityId) => {
  const query = facilityId ? `?facility_id=${facilityId}` : '';
  return await api.get(`/api/trizetto/check-status${query}`);
};

// Worklist APIs
export const fetchWorklist = async (facilityId) => {
  return await api.get(`/api/worklist?facility_id=${facilityId}`);
};

export const fetchWorklistItem = async (claimId) => {
  return await api.get(`/api/worklist/${claimId}`);
};

// `syncEncounter()` was removed in plan Step 8 along with `POST /api/worklist/sync`.
// It was exported here and called from nowhere; leaving it would have left a 404
// waiting for someone to wire up. Encounters now arrive from the facility source
// systems over `POST /api/v1/sync/encounters`, which is machine-to-machine and
// authenticated by credential — not a route this app calls.

export const createManualEncounter = async (data) => {
  return await api.post('/api/worklist/manual', data);
};

export const generateClaim = async (claimId, formData) => {
  return await api.patch(`/api/worklist/${claimId}/generate`, formData);
};

export const generateEdi = async (claimIds) => {
  return await api.post('/api/trizetto/generate-edi', { claimIds });
};

// Coordination of Benefits — Payers
export const fetchClaimDiagnoses = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/diagnoses`);
};

export const fetchClaimServiceLines = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/service-lines`);
};

export const fetchPatientInsurances = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/insurances`);
};

export const updatePatientInsurance = async (patientId, coverageOrder, data) => {
  return await api.put(`/api/patients/${patientId}/insurances/${coverageOrder}`, data);
};

export const deletePatientInsurance = async (patientId, coverageOrder) => {
  return await api.delete(`/api/patients/${patientId}/insurances/${coverageOrder}`);
};

export const reorderPatientInsurance = async (patientId, fromOrder, toOrder) => {
  return await api.patch(`/api/patients/${patientId}/insurances/${fromOrder}/reorder`, { to_order: toOrder });
};

// Atomically swaps two coverage slots in a single server-side transaction —
// no half-reordered state if the request fails mid-flow.
export const swapPatientInsurances = async (patientId, orderA, orderB) => {
  return await api.post(`/api/patients/${patientId}/insurances/swap`, { order_a: orderA, order_b: orderB });
};

// Atomically makes `data` the patient's primary (slot 1): the old primary slides to
// the next open slot or is archived, and the new payer is written to slot 1 — all in
// one server-side transaction, so slot 1 is never left empty on a mid-flow failure.
export const promotePatientInsurancePrimary = async (patientId, data) => {
  return await api.post(`/api/patients/${patientId}/insurances/promote-primary`, data);
};

export const restorePatientInsurance = async (patientId, archivedOrder) => {
  return await api.post(`/api/patients/${patientId}/insurances/${archivedOrder}/restore`);
};

export const updateEncounter = async (claimId, data) => {
  return await api.patch(`/api/worklist/${claimId}/encounter`, data);
};

export const fetchPatientMrns = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/mrns`);
};

export const upsertPatientMrn = async (patientId, facilityId, mrn) => {
  return await api.put(`/api/patients/${patientId}/mrns/${facilityId}`, { mrn });
};

export const deletePatientMrn = async (patientId, facilityId) => {
  return await api.delete(`/api/patients/${patientId}/mrns/${facilityId}`);
};

export const fetchClaimConditionCodes = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/condition-codes`);
};

export const fetchClaimOccurrenceCodes = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/occurrence-codes`);
};

export const fetchClaimValueCodes = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/value-codes`);
};

export const fetchClaimProcedures = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/procedures`);
};

export const fetchClaimChildren = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/children`);
};

export const fetchClaimPayers = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/payers`);
};

// The rebill/correction chain for a claim's level (billed Payer A → withdrawn → Payer B).
export const fetchClaimPayerHistory = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/payer-history`);
};

// Coordination of Benefits — Remits
export const fetchClaimRemits = async (claimId) => {
  return await api.get(`/api/claims/${claimId}/remits`);
};

export const createClaimRemit = async (claimId, remitData) => {
  return await api.post(`/api/claims/${claimId}/remits`, remitData);
};

export const updateClaimRemit = async (claimId, remitId, remitData) => {
  return await api.put(`/api/claims/${claimId}/remits/${remitId}`, remitData);
};

export const deleteClaimRemit = async (claimId, remitId) => {
  return await api.delete(`/api/claims/${claimId}/remits/${remitId}`);
};

// Credit Balances — overpayments owed back to payers (refund/recoupment tracking).
// List returns { data: [...], summary: { count, total_open, bucket_* } }.
export const fetchCreditBalances = async (params = {}) => {
  return await api.get('/api/credit-balances', { params });
};

export const fetchClaimCreditBalances = async (claimId) => {
  return await api.get(`/api/credit-balances/claim/${claimId}`);
};

export const createCreditBalance = async (payload) => {
  return await api.post('/api/credit-balances', payload);
};

// Record a refund/recoupment (or status transition) against an open credit balance.
export const resolveCreditBalance = async (id, payload) => {
  return await api.patch(`/api/credit-balances/${id}/resolve`, payload);
};

// PLB recoupments parsed from ERAs. params.matched = unmatched | matched | all.
export const fetchRecoupments = async (params = {}) => {
  return await api.get('/api/credit-balances/recoupments', { params });
};

// ⟳ Step 14 (S14-e) — refund a PATIENT credit balance. Posts a negative `refund` payment on
// the claim beside the ledger event. payload: { amount, reference, payment_date? }.
export const refundPatientCredit = async (id, payload) => {
  return await api.post(`/api/credit-balances/${id}/patient-refund`, payload);
};

// Manually apply an unmatched ERA recoupment to a chosen credit balance.
export const matchRecoupment = async (id, payload) => {
  return await api.patch(`/api/credit-balances/recoupments/${id}/match`, payload);
};

// Backfill PLB recoupments from already-ingested ERAs (idempotent).
export const reprocessEraPlb = async () => {
  return await api.post('/api/claimmd/era/reprocess-plb', {});
};

// Remit Batch APIs
export const fetchRemitBatches = async () => {
  return await api.get('/api/remit-batches');
};

export const createRemitBatch = async (payload) => {
  return await api.post('/api/remit-batches', payload);
};

export const fetchRemitBatch = async (id) => {
  return await api.get(`/api/remit-batches/${id}`);
};

export const updateRemitBatch = async (id, payload) => {
  return await api.put(`/api/remit-batches/${id}`, payload);
};

export const finalizeRemitBatch = async (id) => {
  return await api.patch(`/api/remit-batches/${id}/finalize`);
};

export const deleteRemitBatch = async (id) => {
  return await api.delete(`/api/remit-batches/${id}`);
};

// Reason-stamped correction of a finalized remit batch. Payload matches the
// normal update shape plus a { reason } string.
export const overrideRemitBatch = async (id, payload) => {
  return await api.patch(`/api/remit-batches/${id}/override`, payload);
};

// Opens the Claim.MD remit PDF for a posted remit batch in a new browser tab.
// The endpoint streams application/pdf; we fetch it as a blob (so the auth
// header is sent) and hand the browser an object URL.
export const openRemitPdf = async (remitBatchId) => {
  const blob = await api.get(
    `/api/claimmd/remit-batches/${remitBatchId}/pdf?download=1`,
    { responseType: 'blob' }
  );
  const url = URL.createObjectURL(blob);
  window.open(url, '_blank', 'noopener');
  setTimeout(() => URL.revokeObjectURL(url), 60000);
};

// Patient search (cross-facility, optional facility filter). ⟳ R30-5: the term
// travels in the BODY of POST /api/patients/search, never in the URL.
// includeArchived surfaces soft-deleted accounts alongside active ones — only for
// screens that act on archived patients (Merge Patients), never the default.
export const searchPatients = async (q, facilityId, { includeArchived } = {}) => {
  const body = { q: q ?? '' };
  if (facilityId)      body.facility_id = facilityId;
  if (includeArchived) body.archived = 'any';
  return await api.post('/api/patients/search', body);
};

// Returns a patient's claims where the given payer is involved,
// with payer_order and existing remit info attached.
export const fetchPatientClaimsForRemit = async (patientId, payerId) => {
  return await api.get(`/api/patients/${patientId}/claims-for-remit?payer_id=${payerId}`);
};

// Returns all patients linked to a given payer (via patient_insurance or claim_payers).
export const fetchPatientsByPayer = async (payerId) => {
  return await api.get(`/api/patients/by-payer/${payerId}`);
};

// Revenue code reference (UB-04 / CMS-1450 Rev Code lookup — code-only picker).
// Pass facilityId to surface this facility's most-used codes first.
export const fetchRevenueCodes = async ({ q = '', facilityId } = {}) => {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (facilityId) params.set('facility_id', facilityId);
  const query = params.toString();
  return await api.get(`/api/revenue-codes${query ? `?${query}` : ''}`);
};

// CPT/HCPCS APIs — auto-fill the service-line description + charge on the claim forms.
// `codes` is an exact lookup (used to resolve the codes already on a claim); it takes
// precedence over `q` on the server.
export const fetchCptCodes = async ({ q = '', facilityId, codes } = {}) => {
  const params = new URLSearchParams();
  if (codes?.length) params.set('codes', codes.join(','));
  else if (q) params.set('q', q);
  if (facilityId) params.set('facility_id', facilityId);
  const query = params.toString();
  return await api.get(`/api/cpt-codes${query ? `?${query}` : ''}`);
};

// Per-facility CPT preferences (pin/hide + custom description/charge overrides).
export const fetchCptCodePreferences = async (facilityId) => {
  return await api.get(`/api/cpt-codes/preferences?facility_id=${facilityId}`);
};

// Prices are effective-dated and append-only (sync plan Step 10). A charge is sent ONLY when
// the caller is changing it: an omitted charge field is left alone by the server, an explicit
// null clears it. `effectiveFrom` (YYYY-MM-DD) dates the new price; the server defaults to today.
export const updateCptCodePreference = async ({
  facilityId, code, pinned, hidden, customDescription, customCharge, effectiveFrom,
}) => {
  const body = {
    facility_id: facilityId, code, pinned, hidden,
    custom_description: customDescription,
  };
  if (customCharge !== undefined) {
    body.custom_charge = customCharge;
    if (effectiveFrom) body.effective_from = effectiveFrom;
  }
  return await api.put('/api/cpt-codes/preferences', body);
};

// Every price window on file for one code — the base schedule and this facility's overrides.
export const fetchCptCodeRates = async (code, facilityId) => {
  const query = facilityId ? `?facility_id=${facilityId}` : '';
  return await api.get(`/api/cpt-codes/${encodeURIComponent(code)}/rates${query}`);
};

// Payer APIs
export const fetchPayers = async (q) => {
  const query = q ? `?q=${encodeURIComponent(q)}` : '';
  return await api.get(`/api/payers${query}`);
};

export const createPayer = async (payerData) => {
  return await api.post('/api/payers', payerData);
};

export const updatePayer = async (id, payerData) => {
  return await api.put(`/api/payers/${id}`, payerData);
};

export const deletePayer = async (id) => {
  return await api.delete(`/api/payers/${id}`);
};

// Payer source mappings (sync plan Step 11, S11-c) — ADMIN-ONLY on the server.
// A facility's source system sends its own payer identifier; these rows decide
// which RCM payer it bills. A change governs the next coverage delivery only.
export const fetchPayerSourceMappings = async (facilityId) => {
  const params = new URLSearchParams({ facility_id: facilityId });
  return await api.get(`/api/payer-source-map?${params.toString()}`);
};

// payload: { facility_id, source_system, source_payer_identifier, payer_id }
export const createPayerSourceMapping = async (payload) => {
  return await api.post('/api/payer-source-map', payload);
};

export const deletePayerSourceMapping = async (id) => {
  return await api.delete(`/api/payer-source-map/${id}`);
};

// Service-key mappings (2026-10-13 amendment, A5) — ADMIN-ONLY on the server. A
// source's `charge_lines` key (`clinic_visit`) → the CPT/HCPCS code a coder should
// consider. A suggestion only: it never writes a claim. The list response also
// carries `unmapped_keys`, the keys this facility has sent that no mapping covers.
export const fetchProcedureSourceMappings = async (facilityId) => {
  const params = new URLSearchParams({ facility_id: facilityId });
  return await api.get(`/api/procedure-source-map?${params.toString()}`);
};

// payload: { facility_id, source_system, service_key, suggested_code, suggested_code_system }
export const createProcedureSourceMapping = async (payload) => {
  return await api.post('/api/procedure-source-map', payload);
};

export const deleteProcedureSourceMapping = async (id) => {
  return await api.delete(`/api/procedure-source-map/${id}`);
};

// Prior-authorization rules (sync plan Step 10a) — ADMIN-ONLY on the server.
// Append-only: saving a rule for a payer and code that already has one starts a
// new rule from its date; retiring makes the requirement unknown again.
export const fetchPriorAuthRules = async ({ payerId, code } = {}) => {
  const params = new URLSearchParams();
  if (payerId) params.set('payer_id', payerId);
  if (code) params.set('code', code);
  const query = params.toString();
  return await api.get(`/api/prior-auth-rules${query ? `?${query}` : ''}`);
};

export const savePriorAuthRule = async (payload) => {
  return await api.post('/api/prior-auth-rules', payload);
};

export const retirePriorAuthRule = async (payload) => {
  return await api.post('/api/prior-auth-rules/retire', payload);
};

// Provider Directory APIs
export const fetchProviders = async (q) => {
  const query = q ? `?q=${encodeURIComponent(q)}` : '';
  return await api.get(`/api/providers${query}`);
};

export const createProvider = async (data) => {
  return await api.post('/api/providers', data);
};

export const updateProvider = async (id, data) => {
  return await api.put(`/api/providers/${id}`, data);
};

export const deleteProvider = async (id) => {
  return await api.delete(`/api/providers/${id}`);
};

// Statement APIs
// facilityId scopes the statement to one billing entity. Omit it to fall back to the
// patient's registered facility, which is the letterhead this has always printed.
export const fetchPatientStatementData = async (patientId, facilityId = null) => {
  const query = facilityId ? `?facility_id=${facilityId}` : '';
  return await api.get(`/api/patients/${patientId}/statement-data${query}`);
};

export const fetchPatientStatements = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/statements`);
};

export const createPatientStatement = async (patientId, statementData) => {
  return await api.post(`/api/patients/${patientId}/statements`, statementData);
};

// Bulk statements — patients with an outstanding balance, full statement data
// for many patients at once, and a one-shot logger for a bulk print run.
export const fetchOutstandingStatements = async (facilityId) => {
  const query = facilityId ? `?facility_id=${facilityId}` : '';
  return await api.get(`/api/patients/statements/outstanding${query}`);
};

// facilityId scopes the payload to one billing entity: its letterhead, its claims, its
// credit. Omitting it falls back to each patient's registered facility.
export const fetchBulkStatementData = async (patientIds, facilityId = null) => {
  return await api.post('/api/patients/statements/bulk-data', {
    patient_ids: patientIds,
    ...(facilityId ? { facility_id: facilityId } : {}),
  });
};

export const createBulkStatements = async (statements) => {
  return await api.post('/api/patients/statements/bulk', { statements });
};

// Patient credit — what is stranded on claims with no responsibility left, and
// the transfers that would move it onto the claims that are still open. Applying is
// an explicit posting, never something a balance query does on its own.
export const fetchPatientCredits = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/credits`);
};

export const applyPatientCredit = async (patientId, body = {}) => {
  return await api.post(`/api/patients/${patientId}/credits/apply`, body);
};

// ── Self-Pay Payment APIs ─────────────────────────────────────────────────────

export const fetchPatientOutstandingClaims = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/claims/outstanding`);
};

export const fetchPatientSelfPayHistory = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/payments`);
};

export const recordSelfPayPayment = async (patientId, data) => {
  return await api.post(`/api/patients/${patientId}/payments`, data);
};

// Reason-stamped edit of a one-time self-pay payment.
export const editSelfPayPayment = async (patientId, paymentId, data) => {
  return await api.put(`/api/patients/${patientId}/payments/${paymentId}`, data);
};

// Soft-void a one-time self-pay payment (reason required).
export const voidSelfPayPayment = async (patientId, paymentId, reason) => {
  return await api.patch(`/api/patients/${patientId}/payments/${paymentId}/void`, { reason });
};

// ── Payment Plan APIs ─────────────────────────────────────────────────────────

export const fetchAllPaymentPlans = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString();
  return await api.get(`/api/payment-plans${qs ? `?${qs}` : ''}`);
};

export const fetchAllSelfPayPayments = async (params = {}) => {
  const qs = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v != null && v !== '')
  ).toString();
  return await api.get(`/api/payment-plans/payments${qs ? `?${qs}` : ''}`);
};

export const fetchPaymentPlanDetail = async (planId) => {
  return await api.get(`/api/payment-plans/${planId}`);
};

export const recordInstallmentPayment = async (planId, instId, data) => {
  return await api.post(`/api/payment-plans/${planId}/installments/${instId}/pay`, data);
};

export const fetchPatientPaymentPlans = async (patientId) => {
  return await api.get(`/api/patients/${patientId}/payment-plans`);
};

export const createPaymentPlan = async (patientId, data) => {
  return await api.post(`/api/patients/${patientId}/payment-plans`, data);
};

// Client Billing APIs

export const fetchClients = async (facilityId) => {
  const query = facilityId ? `?facility_id=${facilityId}` : '';
  return await api.get(`/api/clients${query}`);
};

export const createClient = async (data) => {
  return await api.post('/api/clients', data);
};

export const fetchClientById = async (id) => {
  return await api.get(`/api/clients/${id}`);
};

export const updateClient = async (id, data) => {
  return await api.put(`/api/clients/${id}`, data);
};

export const deleteClient = async (id) => {
  return await api.delete(`/api/clients/${id}`);
};

// Client Service Rates

export const fetchClientRates = async (clientId) => {
  return await api.get(`/api/clients/${clientId}/rates`);
};

export const createClientRate = async (clientId, data) => {
  return await api.post(`/api/clients/${clientId}/rates`, data);
};

export const updateClientRate = async (clientId, rateId, data) => {
  return await api.put(`/api/clients/${clientId}/rates/${rateId}`, data);
};

export const deleteClientRate = async (clientId, rateId) => {
  return await api.delete(`/api/clients/${clientId}/rates/${rateId}`);
};

// Client Invoices

export const fetchClientInvoices = async (clientId) => {
  return await api.get(`/api/clients/${clientId}/invoices`);
};

export const createClientInvoice = async (clientId, data) => {
  return await api.post(`/api/clients/${clientId}/invoices`, data);
};

export const fetchClientInvoice = async (clientId, invoiceId) => {
  return await api.get(`/api/clients/${clientId}/invoices/${invoiceId}`);
};

export const updateClientInvoice = async (clientId, invoiceId, data) => {
  return await api.put(`/api/clients/${clientId}/invoices/${invoiceId}`, data);
};

export const deleteClientInvoice = async (clientId, invoiceId) => {
  return await api.delete(`/api/clients/${clientId}/invoices/${invoiceId}`);
};

// Line Items

export const createClientLineItem = async (clientId, invoiceId, data) => {
  return await api.post(`/api/clients/${clientId}/invoices/${invoiceId}/line-items`, data);
};

export const updateClientLineItem = async (clientId, invoiceId, lineItemId, data) => {
  return await api.put(`/api/clients/${clientId}/invoices/${invoiceId}/line-items/${lineItemId}`, data);
};

export const deleteClientLineItem = async (clientId, invoiceId, lineItemId) => {
  return await api.delete(`/api/clients/${clientId}/invoices/${invoiceId}/line-items/${lineItemId}`);
};

// Client Payments

export const fetchClientPayments = async (clientId) => {
  return await api.get(`/api/clients/${clientId}/payments`);
};

export const createClientPayment = async (clientId, data) => {
  return await api.post(`/api/clients/${clientId}/payments`, data);
};

export const updateClientPayment = async (clientId, paymentId, data) => {
  return await api.put(`/api/clients/${clientId}/payments/${paymentId}`, data);
};

export const deleteClientPayment = async (clientId, paymentId) => {
  return await api.delete(`/api/clients/${clientId}/payments/${paymentId}`);
};

// Send invoice via email with PDF attachment

export const sendClientInvoice = async (clientId, invoiceId, payload) => {
  return await api.post(`/api/clients/${clientId}/invoices/${invoiceId}/send`, payload);
};

// Billing Settings (single-row global config)

export const fetchBillingSettings = async () => {
  return await api.get('/api/billing-settings');
};

export const updateBillingSettings = async (data) => {
  return await api.put('/api/billing-settings', data);
};

export default api;

